import { useEffect, useState, useRef } from "react";
import { api, inr } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, QrCode } from "lucide-react";

export default function BarcodeLabels() {
  const [parts, setParts] = useState([]);
  const [sel, setSel] = useState({});
  const sheetRef = useRef(null);

  useEffect(() => { api.get("/parts").then(r => setParts(r.data)); }, []);

  const toggle = (id) => setSel(s => ({ ...s, [id]: !s[id] }));
  const selectAll = () => {
    const all = {}; parts.forEach(p => all[p.id] = true);
    setSel(all);
  };
  const clear = () => setSel({});

  const chosen = parts.filter(p => sel[p.id]);

  const printSheet = () => {
    if (chosen.length === 0) return;
    window.print();
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 print:hidden">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><QrCode className="w-3 h-3"/> Scannable · Printable</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Barcode Labels</h1>
          <div className="text-sm text-muted-foreground mt-2">Select parts, then print QR labels for shelves & counter scanning</div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="select-all-btn" variant="outline" onClick={selectAll} className="rounded-sm border-border text-xs uppercase tracking-[0.15em]">Select All</Button>
          <Button data-testid="clear-sel-btn" variant="outline" onClick={clear} className="rounded-sm border-border text-xs uppercase tracking-[0.15em]">Clear</Button>
          <Button data-testid="print-labels-btn" onClick={printSheet} disabled={chosen.length === 0} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
            <Printer className="w-4 h-4 mr-1"/> Print {chosen.length || ""}
          </Button>
        </div>
      </div>

      {/* Selection grid (hidden on print) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 print:hidden">
        {parts.map(p => (
          <label
            key={p.id}
            data-testid={`pick-${p.id}`}
            className={`border rounded-sm p-3 flex items-center gap-3 cursor-pointer transition-colors ${sel[p.id] ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
          >
            <Checkbox checked={!!sel[p.id]} onCheckedChange={() => toggle(p.id)} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{p.name}</div>
              <div className="text-[10px] font-mono uppercase text-primary">{p.sku} · {inr(p.price)}</div>
            </div>
            <div className="p-1 bg-white rounded-sm">
              <QRCodeSVG value={JSON.stringify({ sku: p.sku, name: p.name, price: p.price })} size={40} />
            </div>
          </label>
        ))}
      </div>

      {/* Print sheet */}
      <div ref={sheetRef} className="hidden print:block print:bg-white">
        <div className="print-grid">
          {chosen.map(p => (
            <div key={p.id} className="print-label border border-black">
              <div className="text-[8px] uppercase font-bold text-center border-b border-black py-0.5">SynqMoto ERP</div>
              <div className="flex items-center gap-2 p-2">
                <QRCodeSVG value={JSON.stringify({ sku: p.sku, name: p.name, price: p.price })} size={70} />
                <div className="text-black text-xs leading-tight">
                  <div className="font-bold">{p.name}</div>
                  <div className="font-mono text-[10px]">{p.sku}</div>
                  <div className="font-mono">₹{p.price}</div>
                  <div className="text-[9px]">HSN: {p.hsn || "-"} · GST {p.gst_rate}%</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 8mm; }
          body { background: white !important; color: black !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 4mm;
          }
          .print-label {
            page-break-inside: avoid;
            background: white;
            color: black;
            border-radius: 2px;
          }
        }
      `}</style>
    </div>
  );
}
